import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { CircleFlag } from 'react-circle-flags'

import apiClient from '@/api/axios'

import { logger } from '@/lib/logger'

import { useAuthStore } from '@/stores/auth'
import { useLanguageStore } from '@/stores/language'

import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'

export function LanguageSwitcher() {
  const { user } = useAuthStore()
  const { i18n, t } = useTranslation(['common'])
  const { language, setLanguage } = useLanguageStore()
  const currentLang = language.split('-')[0]
  const [changing, setChanging] = useState(false)
  // Supported languages configuration
  const languages = [
    {
      value: 'en',
      label: t('common:languages.en', 'English'),
      countryCode: 'us',
    },
    {
      value: 'ar',
      label: t('common:languages.ar', 'Arabic'),
      countryCode: 'kw',
    },
  ]

  const selected = languages.find((lang) => lang.value === currentLang)

  const handleLanguageChange = async (value: string) => {
    setChanging(true)
    try {
      // Only call API if user is authenticated
      if (user) {
        await apiClient.post('/user-locale', {
          locale: value,
        })
      } else {
        setLanguage(value)
        await i18n.changeLanguage(value)
      }
      // Optional: Add a small delay before reload to ensure language changes are applied
      setTimeout(() => {
        window.location.reload()
      }, 100)
    } catch (err) {
      setChanging(false)
      logger.error('Failed to update language preference:', err)
    }
  }

  return (
    <>
      <Select value={currentLang} onValueChange={handleLanguageChange} disabled={changing}>
        <SelectTrigger className='hover:bg-accent h-8 w-28 cursor-pointer border-none px-2 shadow-none'>
          <div className='flex items-center gap-2'>
            {selected && (
              <CircleFlag
                countryCode={selected?.countryCode}
                className='h-4 w-4 rounded-full'
                style={{ width: 16, height: 16 }}
              />
            )}
            <span className='text-sm'>{selected?.label}</span>
          </div>
        </SelectTrigger>
        <SelectContent>
          {languages.map(({ value, label, countryCode }) => (
            <SelectItem key={value} value={value} className='cursor-pointer'>
              <div className='flex items-center gap-2'>
                <CircleFlag
                  countryCode={countryCode}
                  className='h-4 w-4 rounded-full'
                  style={{ width: 16, height: 16 }}
                />
                <span>{label}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  )
}
